import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiPlay, FiTarget, FiBrain } = FiIcons;

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const stats = [
    {
      number: '48.4M',
      label: 'Americans with SUD',
      color: 'from-accent-magenta to-accent-purple'
    },
    {
      number: '25-70%',
      label: 'Relapse reduction',
      color: 'from-accent-gold to-accent-yellow'
    },
    {
      number: '85%',
      label: 'Feel in control',
      color: 'from-accent-lavender to-accent-light'
    }
  ];

  return (
    <section className="pt-24 pb-20 bg-gradient-to-br from-accent-light via-white to-accent-purple/20">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block bg-accent-magenta/10 text-accent-magenta px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              Break the Cycle. Build the Skills.
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-4xl lg:text-6xl font-playfair font-bold text-gray-900 mb-6 leading-tight"
            >
              Real Recovery{' '}
              <span className="bg-gradient-to-r from-accent-magenta to-accent-purple bg-clip-text text-transparent">
                Starts Here
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg lg:text-xl text-gray-700 mb-8 leading-relaxed"
            >
              Tired of white-knuckling through cravings? Relapse isn't a moral failure. It's often a cognitive one. We provide the executive function therapy that builds the brain skills needed for sustainable recovery—managing triggers, emotions, and the stress of life.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="space-y-4 mb-8"
            >
              <p className="text-gray-700">
                You've done the hard work of detox and rehab. You've been to meetings. You want to stay sober. But when stress hits, old patterns take over. Why?
              </p>

              <p className="text-gray-700">
                <strong>Addiction rewires the brain's executive function system</strong>—its ability to make decisions, regulate emotions, and plan for the future.
              </p>

              <div className="bg-accent-light/50 p-4 rounded-lg border-l-4 border-accent-magenta">
                <p className="text-gray-800 font-medium">
                  You're not weak. You're not broken. Your willpower isn't the problem.
                </p>
                <p className="text-gray-700 mt-2">
                  Alone nobody can fix a neurological deficit.
                </p>
              </div>

              <p className="text-gray-700 font-medium text-lg">
                We don't just help you say no to substances. We train your brain to say yes to the life you actually want.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                onClick={() => scrollToSection('contact')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-accent-magenta/90 transition-colors duration-200"
              >
                Book Your Recovery Breakthrough Call
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.button>

              <motion.a
                href="https://www.wethebillions.com/quiz"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-accent-magenta text-accent-magenta px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-accent-magenta hover:text-white transition-all duration-200"
              >
                Take FREE Quiz
                <SafeIcon icon={FiPlay} className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&h=400&fit=crop&auto=format&q=80"
                alt="Person in recovery feeling empowered and confident"
                className="w-full h-96 object-cover rounded-3xl shadow-2xl"
              />

              {/* Floating Stats */}
              <div className="absolute -bottom-6 -left-6 space-y-4">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                    className={`bg-gradient-to-r ${stat.color} p-4 rounded-2xl text-white shadow-lg backdrop-blur-sm`}
                  >
                    <div className="text-2xl font-bold">{stat.number}</div>
                    <div className="text-sm opacity-90">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Brain Icon */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1.2 }}
                className="absolute -top-6 -right-6 bg-white p-4 rounded-full shadow-lg"
              >
                <SafeIcon icon={FiBrain} className="w-8 h-8 text-accent-magenta" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;